import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { TourniquetButtonContent } from '../tourniquet/TourniquetButtonContent';
import { TourniquetAnswersContainer, TourniquetCard, TourniquetContent, TourniquetTrainingRow } from '../tourniquet/TourniquetStyles';
import { QuestionDto } from '@api/models/questionnaire/QuestionDto';
import { AnswerDto } from '@api/models/questionnaire/AnswerDto';
import { BASE_URL_WITH_VERSION } from '../../utils/api';
import { getMediaToken } from '../../utils/storage';
import { ShowImageButton } from '../invitation/Detail/Tabs/ShowImageButton';
import { Box } from '@mui/material';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';
import { AnswerResponseType } from '@api/models/questionnaire/AnswerResponseType';

export interface TrainingRunQuestion extends QuestionDto {
  answerResponse?: AnswerResponseType;
  selectedAnswerId?: string;
}

interface Props {
  questions: TrainingRunQuestion[];
  questionIndex: number;
  onAnswerSelected: (questionId: string, answerId: string) => void;
  onAdvance: () => void;
  onPrev?: () => void;
  onForward?: () => void;
  onAbort: () => void;
}

export const TrainingRunQuestionnaire: FC<Props> = ({
  questions,
  questionIndex,
  onAnswerSelected,
  onAdvance,
  onPrev,
  onForward,
  onAbort,
}) => {
  const { t } = useTranslation();
  const mediaToken = getMediaToken();

  const question = questions[questionIndex];

  const handleClick = (item: AnswerDto) => {
    onAnswerSelected(question.id!, item.id!);
    onAdvance();
  };

  return (
    <TourniquetButtonContent
      backOnClick={onPrev}
      backDisabled={!onPrev}
      nextOnClick={onForward}
      nextDisabled={!onForward}
      onAbort={onAbort}
    >
      <TourniquetCard>
        <TourniquetContent>
          <QcsTypography variant="h4" align="center" sx={{ m: 2 }}>
            {question.content}
          </QcsTypography>
          {question.document && (
            <Box
              sx={{
                width: '100%',
                margin: '1rem 0',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ShowImageButton tooltipTitle="" documentUrl={`${BASE_URL_WITH_VERSION}${question.document.path}?token=${mediaToken}`}>
                <img
                  src={`${BASE_URL_WITH_VERSION}${question.document.path}?token=${mediaToken}`}
                  alt="Question"
                  style={{
                    maxHeight: '200px',
                    maxWidth: '100%',
                    cursor: 'pointer',
                    objectFit: 'contain',
                  }}
                />
              </ShowImageButton>
            </Box>
          )}
          <TourniquetAnswersContainer>
            {question.answers?.map((x) => (
              <TourniquetTrainingRow key={x.id}>
                <QcsTypography variant="h6">{x.content}</QcsTypography>
                <QcsButton
                  variant={question.selectedAnswerId === x.id ? 'contained' : 'outlined'}
                  onClick={() => handleClick(x)}
                  sx={{ flex: '0 0 auto' }}
                >
                  {t('html.select')}
                </QcsButton>
              </TourniquetTrainingRow>
            ))}
          </TourniquetAnswersContainer>
        </TourniquetContent>
      </TourniquetCard>
      <ShowError resource={[resources.submitTrainingAnswers]} />
    </TourniquetButtonContent>
  );
};
